import { useQuery } from '@apollo/client'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_CATEGORIES,
  GET_TENANT_ARRAY_SETTINGS,
  GET_TENANT_POS_STORE_IDS,
  GET_TENANT_STORES,
  GetPosCategoriesQuery,
  GetPosCategoriesQueryVariables,
  GetTenantActionGroupsQueryVariables,
  getTenantArraySettingsQuery,
  getTenantArraySettingsQueryVariables,
  GetTenantPOSStoreIdsQuery,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
} from '@/graphql'

export interface POSDataSetupStatus {
  hasDataInBigQuery: boolean
  needToSetUpCategories: boolean
  needToSetUpInsideSalesCategories: boolean
  needToSetOutsideSalesCategories: boolean
  needToSetFuelSalesCategories: boolean
  numUnlinkedStores: number
  noLinkedStores: boolean
  loading: boolean
}

export const usePosDataSetupStatus = ({
  storeIds,
}: {
  storeIds: string[] | undefined
}): POSDataSetupStatus => {
  const _tenantStores = useQuery<
    GetTenantStoresQuery,
    GetTenantStoresQueryVariables
  >(GET_TENANT_STORES, {
    notifyOnNetworkStatusChange: true,
    variables: { pagination: { limit: 100, offset: 0 } },
  })

  const tenantStores = storeIds?.length
    ? _tenantStores.data?.tenantStores?.results.filter(store =>
        storeIds.includes(store.id),
      )
    : _tenantStores.data?.tenantStores?.results

  const tenantPosStoreIds = useQuery<
    GetTenantPOSStoreIdsQuery,
    GetTenantActionGroupsQueryVariables
  >(GET_TENANT_POS_STORE_IDS, { notifyOnNetworkStatusChange: true })

  const hasDataInBigQuery =
    !!tenantPosStoreIds.data?.tenantPosStoreIds?.length ||
    tenantPosStoreIds.loading // assume we have data in bigquery until proven wrong

  const numUnlinkedStores =
    !hasDataInBigQuery || tenantPosStoreIds.loading
      ? 0
      : tenantStores?.filter(store => !store.externalStoreId).length || 0

  const noLinkedStores = _tenantStores.loading
    ? false
    : !!tenantStores?.every(store => !store.externalStoreId)

  const tenantArraySettings = useQuery<
    getTenantArraySettingsQuery,
    getTenantArraySettingsQueryVariables
  >(GET_TENANT_ARRAY_SETTINGS)

  const hasInsideSalesCategories =
    !!tenantArraySettings?.data?.tenantArraySettings?.insideSalesCategories
      ?.length
  const hasOutsideSalesCategories =
    !!tenantArraySettings?.data?.tenantArraySettings?.outsideSalesCategories
      ?.length
  const hasFuelSalesCategories =
    !!tenantArraySettings?.data?.tenantArraySettings?.fuelSalesCategories
      ?.length

  const hasAllCategories =
    hasInsideSalesCategories &&
    hasOutsideSalesCategories &&
    hasFuelSalesCategories

  const posCategories = useQuery<
    GetPosCategoriesQuery,
    GetPosCategoriesQueryVariables
  >(GET_POS_CATEGORIES, {
    variables: { pagination: { limit: 100, offset: 0 } },
  })

  const refetch = () => {
    _tenantStores.refetch()
    tenantArraySettings.refetch()
    posCategories.refetch()
    tenantPosStoreIds.refetch()
  }

  useRefresh(refetch)

  return {
    hasDataInBigQuery,
    numUnlinkedStores,
    noLinkedStores,
    needToSetUpCategories:
      hasDataInBigQuery && !hasAllCategories && !tenantArraySettings.loading,
    needToSetUpInsideSalesCategories:
      hasDataInBigQuery &&
      !hasInsideSalesCategories &&
      !tenantArraySettings.loading,
    needToSetOutsideSalesCategories:
      hasDataInBigQuery &&
      !hasOutsideSalesCategories &&
      !tenantArraySettings.loading,
    needToSetFuelSalesCategories:
      hasDataInBigQuery &&
      !hasFuelSalesCategories &&
      !tenantArraySettings.loading,
    loading:
      _tenantStores.loading ||
      tenantArraySettings.loading ||
      posCategories.loading ||
      tenantPosStoreIds.loading,
  }
}
